import React, { useMemo, useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box,
    Button, Stack, TextField, FormControl, InputLabel,
    Select, MenuItem, Typography, Divider, IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useApiClient } from '../../components/useApiClient';
import { useUser } from '../../components/UserContext';
import useAuthToken from '../../functions/useAuthToken';
import TimeOffsetCheckboxGroup from "./ReminderOffsetsSelector";

const DiaryCategory = { Payment: 20 } as const;
const LinkedEntityType = { Artwork: 1 } as const;

// Simple frequency options for v1
type Frequency = "WEEKLY" | "MONTHLY";

type MilestoneRow = {
    sequenceNumber: number;
    label?: string;
    dueLocal: string; // "YYYY-MM-DD"
    amount: number;
};

function toUtcIsoFromLocalDate(dateOnly: string): string {
    // Treat as midnight UTC for now. If you want local midnight, adjust later.
    return new Date(`${dateOnly}T00:00:00Z`).toISOString();
}

function addMonths(date: Date, months: number) {
    const d = new Date(date);
    d.setUTCMonth(d.getUTCMonth() + months);
    return d;
}

function addWeeks(date: Date, weeks: number) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    return d;
}

function generateEqualMilestones(
    total: number,
    count: number,
    startLocal: string,
    frequency: Frequency
): MilestoneRow[] {
    const start = new Date(`${startLocal}T00:00:00Z`);
    const base = Math.max(1, count);
    const per = total / base;

    // Ensure currency rounding doesn’t drift: allocate remainder to last payment
    const rounded = (x: number) => Math.round(x * 100) / 100;

    const rows: MilestoneRow[] = [];
    let running = 0;

    for (let i = 1; i <= base; i++) {
        const due = frequency === "MONTHLY"
            ? addMonths(start, i - 1)
            : addWeeks(start, i - 1);

        const amount = i === base ? rounded(total - running) : rounded(per);
        running += amount;

        rows.push({
            sequenceNumber: i,
            label: i === 1 ? "Deposit" : (i === base ? "Final payment" : `Payment ${i}`),
            dueLocal: due.toISOString().substring(0, 10),
            amount
        });
    }

    return rows;
}

type Props = {
    open: boolean;
    artworkId: number;
    artworkTitle?: string;
    valuationDefault: number;
    currencyCodeDefault: string;
    onClose: () => void;
    onCreated: (result: { paymentScheduleId: number; parentDiaryItemId: number }) => void;
};

export default function ArtworkPaymentScheduleDialog({
    open,
    artworkId,
    artworkTitle,
    valuationDefault,
    currencyCodeDefault,
    onClose,
    onCreated,
}: Props) {
    //API Calls
    const { activeClientId, isAdmin, isOwner } = useUser();
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    async function createPaymentSchedule(dto: any) {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token in createPaymentSchedule');

        const res = await postJson("payment-schedules", accessToken, JSON.stringify(dto));
        return res as { paymentScheduleId: number; parentDiaryItemId: number };
    }

    const [title, setTitle] = useState<string>(
        artworkTitle ? `Payment schedule — ${artworkTitle}` : "Payment schedule"
    );

    const [currencyCode, setCurrencyCode] = useState<string>(currencyCodeDefault);
    const [totalAmount, setTotalAmount] = useState<number>(valuationDefault);

    // Mode
    const [mode, setMode] = useState<"EQUAL" | "CUSTOM">("EQUAL");

    // Equal split settings
    const [paymentCount, setPaymentCount] = useState<number>(3);
    const [startLocal, setStartLocal] = useState<string>(() => {
        const today = new Date().toISOString().substring(0, 10);
        return today;
    });
    const [frequency, setFrequency] = useState<Frequency>("MONTHLY");

    // Reminder offsets (minutes)
    const [reminders, setReminders] = useState<number[]>([-10080, -1440, 0]);

    // Custom milestones
    const [milestones, setMilestones] = useState<MilestoneRow[]>(() => [
        { sequenceNumber: 1, label: "Deposit", dueLocal: new Date().toISOString().substring(0, 10), amount: 0 },
    ]);

    const equalPreview = useMemo(() => {
        if (mode !== "EQUAL") return [];
        if (!totalAmount || totalAmount <= 0) return [];
        return generateEqualMilestones(totalAmount, paymentCount, startLocal, frequency);
    }, [mode, totalAmount, paymentCount, startLocal, frequency]);

    useEffect(() => {
        if (totalAmount === 0) {
            setTotalAmount(valuationDefault);
            setCurrencyCode(currencyCodeDefault);
        }
    }, [valuationDefault]);

    function validate(rows: MilestoneRow[]): string | null {
        if (!title.trim()) return "Title is required.";
        if (!currencyCode.trim()) return "Currency code is required.";
        if (!rows.length) return "At least one milestone is required.";

        for (const r of rows) {
            if (!r.dueLocal) return "Each milestone must have a due date.";
            if (!(r.amount > 0)) return "Each milestone amount must be greater than 0.";
        }

        // Optional: enforce sum matches totalAmount if totalAmount provided
        const sum = rows.reduce((a, b) => a + (b.amount || 0), 0);
        const rounded = Math.round(sum * 100) / 100;
        const t = Math.round((totalAmount || 0) * 100) / 100;
        if (totalAmount > 0 && rounded !== t) {
            return `Milestones total (${rounded.toFixed(2)}) must equal Total amount (${t.toFixed(2)}).`;
        }

        const primaryRemindersOk = reminders.every(x => Number.isFinite(x));
        if (!primaryRemindersOk) return "Reminder offsets are invalid.";

        return null;
    }

    async function handleSubmit() {
        const rows = mode === "EQUAL" ? equalPreview : milestones;
        const err = validate(rows);
        if (err) {
            alert(err);
            return;
        }

        const dto = {
            title,
            description: null,
            currencyCode,
            totalAmount: totalAmount > 0 ? totalAmount : null,

            visibility: 2, // Client wide by default - will limit view by role;
            category: DiaryCategory.Payment,

            links: [
                {
                    entityType: LinkedEntityType.Artwork,
                    entityId: artworkId.toString(),
                    label: artworkTitle ?? `Artwork ${artworkId}`,
                    isPrimary: true,
                },
            ],

            milestones: rows.map(r => ({
                sequenceNumber: r.sequenceNumber,
                label: r.label ?? null,
                dueUtc: toUtcIsoFromLocalDate(r.dueLocal),
                amount: r.amount,
            })),

            reminderOffsetsMinutes: reminders,
        };

        const result = await createPaymentSchedule(dto);
        onCreated(result);
    }

    function setMilestoneField(idx: number, patch: Partial<MilestoneRow>) {
        setMilestones(prev => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    }

    function addMilestoneRow() {
        setMilestones(prev => {
            const nextSeq = prev.length + 1;
            return [
                ...prev,
                {
                    sequenceNumber: nextSeq,
                    label: `Payment ${nextSeq}`,
                    dueLocal: prev[prev.length - 1]?.dueLocal ?? new Date().toISOString().substring(0, 10),
                    amount: 0,
                },
            ];
        });
    }

    function removeMilestoneRow(idx: number) {
        setMilestones(prev => {
            const next = prev.filter((_, i) => i !== idx);
            // re-sequence
            return next.map((m, i) => ({ ...m, sequenceNumber: i + 1 }));
        });
    }

    const rowsToShow = mode === "EQUAL" ? equalPreview : milestones;
    const totalOfRows = rowsToShow.reduce((a, b) => a + (b.amount || 0), 0);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Create payment schedule</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Schedule title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="Total amount"
                            type="number"
                            value={totalAmount}
                            onChange={(e) => setTotalAmount(Number(e.target.value || 0))}
                            fullWidth
                            inputProps={{ min: 0, step: "0.01" }}
                        />

                        <TextField
                            label="Currency"
                            value={currencyCode}
                            onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
                            fullWidth
                        />
                    </Stack>

                    <Divider />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                        <FormControl fullWidth>
                            <InputLabel>Milestone mode</InputLabel>
                            <Select
                                label="Milestone mode"
                                value={mode}
                                onChange={(e) => setMode(e.target.value as any)}
                            >
                                <MenuItem value="EQUAL">Equal split</MenuItem>
                                <MenuItem value="CUSTOM">Custom milestones</MenuItem>
                            </Select>
                        </FormControl>

                        {mode === "EQUAL" && (
                            <>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton
                                        onClick={() => setPaymentCount((prev) => Math.max(1, prev - 1))}
                                        disabled={paymentCount <= 1}
                                        size="small"
                                    >
                                        <RemoveIcon />
                                    </IconButton>

                                    <TextField
                                        label="Number of payments"
                                        type="number"
                                        value={paymentCount}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") return;
                                            const next = Number(raw);
                                            if (!Number.isNaN(next)) {
                                                setPaymentCount(Math.max(1, next));
                                            }
                                        }}
                                        inputProps={{
                                            min: 1,
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            style: { textAlign: "center" }
                                        }}
                                        sx={{ width: 120 }}
                                        size="small"
                                    />

                                    <IconButton
                                        onClick={() => setPaymentCount((prev) => prev + 1)}
                                        size="small"
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>

                                <TextField
                                    label="Start date (first due date)"
                                    type="date"
                                    value={startLocal}
                                    onChange={(e) => setStartLocal(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />

                                <FormControl fullWidth>
                                    <InputLabel>Frequency</InputLabel>
                                    <Select
                                        label="Frequency"
                                        value={frequency}
                                        onChange={(e) => setFrequency(e.target.value as Frequency)}
                                    >
                                        <MenuItem value="WEEKLY">Weekly</MenuItem>
                                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Stack>

                    {mode === "CUSTOM" && (
                        <Stack direction="row" justifyContent="flex-end">
                            <Button startIcon={<AddIcon />} variant="outlined" onClick={addMilestoneRow}>
                                Add milestone
                            </Button>
                        </Stack>
                    )}

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Milestones
                    </Typography>

                    <Stack spacing={1.5}>
                        {rowsToShow.map((m, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                    }}
                                >
                                    <TextField
                                        label="#"
                                        value={m.sequenceNumber}
                                        disabled
                                        size="small"
                                        sx={{ width: 70 }}
                                    />

                                    {mode === "CUSTOM" && (
                                        <IconButton
                                            onClick={() => removeMilestoneRow(idx)}
                                            disabled={rowsToShow.length <= 1}
                                            aria-label="Remove milestone"
                                            size="small"
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "minmax(0, 1fr) 180px 160px",
                                        },
                                        gap: 1.5,
                                        alignItems: "start",
                                    }}
                                >
                                    <TextField
                                        label="Label"
                                        value={m.label ?? ""}
                                        onChange={(e) =>
                                            mode === "CUSTOM" &&
                                            setMilestoneField(idx, { label: e.target.value })
                                        }
                                        disabled={mode !== "CUSTOM"}
                                        fullWidth
                                        size="small"
                                    />

                                    <TextField
                                        label="Due date"
                                        type="date"
                                        value={m.dueLocal}
                                        onChange={(e) =>
                                            mode === "CUSTOM" &&
                                            setMilestoneField(idx, { dueLocal: e.target.value })
                                        }
                                        disabled={mode !== "CUSTOM"}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        size="small"
                                    />

                                    <TextField
                                        label="Amount"
                                        type="number"
                                        value={m.amount}
                                        onChange={(e) =>
                                            mode === "CUSTOM" &&
                                            setMilestoneField(idx, {
                                                amount: Number(e.target.value || 0),
                                            })
                                        }
                                        disabled={mode !== "CUSTOM"}
                                        inputProps={{ min: 0, step: "0.01" }}
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Milestones total: {totalOfRows.toFixed(2)} {currencyCode}
                    </Typography>

                    <Divider />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Reminders (minutes offset from due date)
                    </Typography>

                    

                    <TimeOffsetCheckboxGroup
                        value={reminders}
                        onChange={setReminders}
                        label="Set reminder offsets"
                    />

                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit}>
                    Create schedule
                </Button>
            </DialogActions>
        </Dialog>
    );
}
